/**
 * utils/discoveryScope.ts — FRONTEND MIRROR of supabase/functions/server/discoveryScopeCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 */


export type DescopeDestination = 'change_order' | 'engagement' | 'client' | 'dropped';

export interface DescopeMark {
  to: DescopeDestination;
  /** CO ref, engagement id, or free text */
  ref?: string;
  label?: string;
  reason?: string;
  at: string;
  by: string;
  byName?: string;
  /** The engagement this cell left; null when marked outside any engagement */
  fromEngagementId: string | null;
}

export interface CarryMark {
  fromEngagementId: string | null;
  toEngagementId: string | null;
  viaDocument: 'change_order' | 'engagement';
  ref?: string;
  label?: string;
  at: string;
  by: string;
  byName?: string;
}

export interface ScopeMarks {
  descoped?: DescopeMark;
  descopedQuestions?: Record<string, DescopeMark | undefined>;
  /** keyed by target engagement id ('none' when unknown) */
  carried?: Record<string, CarryMark | undefined>;
  carriedQuestions?: Record<string, Record<string, CarryMark | undefined> | undefined>;
}

export interface ScopeActor {
  userId: string;
  userName?: string;
}

export const DESCOPE_LABELS: Record<DescopeDestination, string> = {
  change_order: 'Moved to a change order',
  engagement: 'Moved to a later engagement',
  client: "Handed to the client's team",
  dropped: 'Dropped',
};

const engKey = (id: string | null | undefined) => id ?? 'none';

function cloneMarks(s: ScopeMarks | null | undefined): ScopeMarks {
  return {
    ...(s ?? {}),
    descopedQuestions: { ...(s?.descopedQuestions ?? {}) },
    carried: { ...(s?.carried ?? {}) },
    carriedQuestions: Object.fromEntries(Object.entries(s?.carriedQuestions ?? {}).map(([k, v]) => [k, { ...(v ?? {}) }])),
  };
}

export function makeDescopeMark(input: { to: DescopeDestination; ref?: string; label?: string; reason?: string; fromEngagementId: string | null }, actor: ScopeActor, now = new Date().toISOString()): DescopeMark {
  const reason = (input.reason ?? '').trim();
  if (input.to === 'dropped' && !reason) throw new Error('A reason is required to drop scope');
  return {
    to: input.to,
    ref: input.ref?.trim() || undefined,
    label: input.label?.trim() || DESCOPE_LABELS[input.to],
    reason: reason || undefined,
    at: now,
    by: actor.userId,
    byName: actor.userName,
    fromEngagementId: input.fromEngagementId ?? null,
  };
}

export function descopeCell(scope: ScopeMarks | null | undefined, questionId: string, mark: DescopeMark): ScopeMarks {
  const next = cloneMarks(scope);
  next.descopedQuestions![questionId] = mark;
  return next;
}

export function descopeBlock(scope: ScopeMarks | null | undefined, mark: DescopeMark): ScopeMarks {
  const next = cloneMarks(scope);
  next.descoped = mark;
  return next;
}

export function restoreCell(scope: ScopeMarks | null | undefined, questionId: string): ScopeMarks {
  const next = cloneMarks(scope);
  delete next.descopedQuestions![questionId];
  return next;
}

export function restoreBlock(scope: ScopeMarks | null | undefined): ScopeMarks {
  const next = cloneMarks(scope);
  delete next.descoped;
  next.descopedQuestions = {};
  return next;
}

export function carryCell(scope: ScopeMarks | null | undefined, questionId: string, mark: CarryMark): ScopeMarks {
  const next = cloneMarks(scope);
  const k = engKey(mark.toEngagementId);
  next.carriedQuestions![questionId] = { ...(next.carriedQuestions![questionId] ?? {}), [k]: mark };
  // A carry into an engagement undoes a descope made from that same engagement.
  const d = next.descopedQuestions![questionId];
  if (d && engKey(d.fromEngagementId) === k) delete next.descopedQuestions![questionId];
  return next;
}

export function carryBlock(scope: ScopeMarks | null | undefined, mark: CarryMark): ScopeMarks {
  const next = cloneMarks(scope);
  const k = engKey(mark.toEngagementId);
  next.carried![k] = mark;
  if (next.descoped && engKey(next.descoped.fromEngagementId) === k) delete next.descoped;
  return next;
}

export interface EffectiveScope {
  descoped: DescopeMark | null;
  carried: CarryMark | null;
}

/**
 * What a cell's scope is for one engagement: descoped when a mark on the cell
 * or its block was made from this engagement (or from no engagement); carried
 * when a carry mark targets this engagement and nothing descoped it from here.
 */
export function effectiveScope(scope: ScopeMarks | null | undefined, questionId: string | null, engagementId: string | null): EffectiveScope {
  if (!scope) return { descoped: null, carried: null };
  const k = engKey(engagementId);
  // With no engagement in view (company-level), every mark applies.
  const appliesHere = (from: string | null | undefined) => engagementId === null || from === null || from === undefined || engKey(from) === k;

  const carried = (questionId ? scope.carriedQuestions?.[questionId]?.[k] : undefined) ?? scope.carried?.[k] ?? null;
  const cellMark = questionId ? scope.descopedQuestions?.[questionId] : undefined;
  const blockMark = scope.descoped;
  const descoped = (cellMark && appliesHere(cellMark.fromEngagementId) ? cellMark : null) ?? (blockMark && appliesHere(blockMark.fromEngagementId) ? blockMark : null);
  if (carried && descoped && engKey(descoped.fromEngagementId) === k && descoped.at <= carried.at) {
    return { descoped: null, carried };
  }
  return { descoped: descoped ?? null, carried: carried ?? null };
}

// ---------- Reconciliation (spec §4.10 step 2–4) ----------

export type ReconcileOutcome = 'carry' | 'leave' | 'client' | 'drop';

export interface ReconcileDecision {
  /** one of the two */
  cellId?: string;
  blockId?: string;
  outcome: ReconcileOutcome;
  reason?: string;
  /** Display text of the cell or block, kept on the scope map so summaries can name it */
  text?: string;
  /** The new document's scope item this decision satisfies (spec §4.10 step 1), when proposed or confirmed */
  scopeItemId?: string;
  scopeItemLabel?: string;
}

export interface ReconcileTarget {
  type: 'engagement' | 'change_order';
  /** target engagement id (for both types: a CO belongs to an engagement) */
  engagementId: string | null;
  /** CO ref when type is change_order */
  ref?: string;
  label?: string;
}

export interface ScopeMapEntry extends ReconcileDecision {
  at: string;
  by: string;
  byName?: string;
}

export function validateDecisions(decisions: ReconcileDecision[]): string[] {
  const errors: string[] = [];
  decisions.forEach((d, i) => {
    if (!d.cellId && !d.blockId) errors.push(`decision ${i}: cellId or blockId required`);
    if (!['carry', 'leave', 'client', 'drop'].includes(d.outcome)) errors.push(`decision ${i}: unknown outcome '${d.outcome}'`);
    if (d.outcome === 'drop' && !(d.reason ?? '').trim()) errors.push(`decision ${i}: a reason is required to drop`);
  });
  return errors;
}

/** Marks to apply for one decision, given where the cell is leaving from and going to. */
export function marksForDecision(d: ReconcileDecision, target: ReconcileTarget, fromEngagementId: string | null, actor: ScopeActor, now = new Date().toISOString()): { descope?: DescopeMark; carry?: CarryMark } {
  switch (d.outcome) {
    case 'carry':
      return {
        descope: fromEngagementId && fromEngagementId !== target.engagementId
          ? makeDescopeMark({ to: target.type, ref: target.ref ?? target.engagementId ?? undefined, label: target.label, fromEngagementId }, actor, now)
          : undefined,
        carry: { fromEngagementId, toEngagementId: target.engagementId, viaDocument: target.type, ref: target.ref, label: target.label, at: now, by: actor.userId, byName: actor.userName },
      };
    case 'client':
      return { descope: makeDescopeMark({ to: 'client', reason: d.reason, fromEngagementId }, actor, now) };
    case 'drop':
      return { descope: makeDescopeMark({ to: 'dropped', reason: d.reason, fromEngagementId }, actor, now) };
    default:
      return {};
  }
}
